import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import Lf from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Pencil, X, LocateFixed, Search, Loader2, ExternalLink, Check } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const UAE = [24.4539, 54.3773];
const TILES = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const PIN = Lf.divIcon({
  className: "", iconSize: [36, 46], iconAnchor: [18, 44],
  html: '<div style="width:36px;height:46px;display:flex;justify-content:center"><svg width="36" height="46" viewBox="0 0 24 30"><path d="M12 0C5.4 0 0 5.2 0 11.7 0 20.4 12 30 12 30s12-9.6 12-18.3C24 5.2 18.6 0 12 0z" fill="#064E3B"/><circle cx="12" cy="11.5" r="4.6" fill="#fff"/></svg></div>',
});
export const mapLink = (p) => `https://www.google.com/maps?q=${p[0].toFixed(6)},${p[1].toFixed(6)}`;
export function parseLatLng(lat, lng, url) {
  if (lat != null && lng != null && lat !== "" && lng !== "") return [Number(lat), Number(lng)];
  const m = String(url || "").match(/(?:q=|@|ll=|query=)(-?\d+\.\d+),\s*(-?\d+\.\d+)/);
  return m ? [Number(m[1]), Number(m[2])] : null;
}

function Picker({ pos, setPos }) {
  const map = useMap();
  useEffect(() => { const t = setTimeout(() => map.invalidateSize(), 250); return () => clearTimeout(t); }, [map]);
  useMapEvents({ click: (e) => setPos([e.latlng.lat, e.latlng.lng]) });
  return pos ? <Marker position={pos} icon={PIN} draggable eventHandlers={{ dragend: (e) => { const p = e.target.getLatLng(); setPos([p.lat, p.lng]); } }} /> : null;
}

function FlyTo({ target }) {
  const map = useMap();
  useEffect(() => { if (target) map.flyTo(target, 16, { duration: 0.8 }); }, [target, map]);
  return null;
}

function MapSearch({ onPick, L, lang }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const [busy, setBusy] = useState(false);
  const run = async (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    setBusy(true);
    try {
      const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=5&countrycodes=ae&accept-language=${lang}&q=${encodeURIComponent(q)}`);
      const d = await r.json();
      setRes(d);
      if (!d.length) toast.error(L("لم يتم العثور على نتائج", "No results found"));
    } catch { toast.error(L("تعذّر البحث", "Search failed")); }
    setBusy(false);
  };
  return (
    <div className="absolute top-3 inset-x-3 z-[1000]">
      <form onSubmit={run} className="flex items-center gap-2 bg-white rounded-2xl shadow-lg border border-slate-200 px-3 h-12">
        <Search className="w-4 h-4 text-slate-400 shrink-0" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={L("ابحث عن منطقة أو مبنى...", "Search an area or building...")} className="flex-1 h-full bg-transparent outline-none text-sm" data-testid="office-map-search-input" />
        <button type="submit" className="text-sm font-bold text-[#064E3B]" data-testid="office-map-search-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : L("بحث", "Search")}</button>
      </form>
      {res.length > 0 && (
        <div className="mt-1.5 bg-white rounded-2xl shadow-lg border border-slate-200 max-h-56 overflow-y-auto" data-testid="office-map-search-results">
          {res.map((r) => (
            <button key={r.place_id} type="button" onClick={() => { onPick([Number(r.lat), Number(r.lon)]); setRes([]); }} className="w-full text-start px-4 py-2.5 text-sm hover:bg-[#F4F2EC] border-b last:border-0 border-slate-100">{r.display_name}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function MapDialog({ initial, onSave, onClose, L, lang }) {
  const [pos, setPos] = useState(initial);
  const [fly, setFly] = useState(null);
  const [locating, setLocating] = useState(false);
  const pick = (p) => { setPos(p); setFly(p); };
  const locate = () => {
    if (!navigator.geolocation) return toast.error(L("المتصفح لا يدعم تحديد الموقع", "Location not supported"));
    setLocating(true);
    navigator.geolocation.getCurrentPosition((g) => { pick([g.coords.latitude, g.coords.longitude]); setLocating(false); },
      () => { toast.error(L("تعذّر تحديد موقعك الحالي", "Couldn't get your location")); setLocating(false); }, { enableHighAccuracy: true, timeout: 10000 });
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl w-[96vw] p-0 overflow-hidden rounded-3xl gap-0" data-testid="office-map-dialog">
        <div className="px-5 py-4 border-b border-slate-100">
          <DialogTitle className="font-bold text-lg text-start">{L("حدّد موقع المكتب", "Pick the office location")}</DialogTitle>
          <p className="text-xs text-slate-500 mt-1">{L("حرّك الخارطة وكبّرها، ثم اضغط على المكان الصحيح أو اسحب الدبوس لضبطه بدقة.", "Pan and zoom the map, then tap the exact spot or drag the pin to adjust it.")}</p>
        </div>
        <div className="relative h-[60vh] min-h-[340px]">
          <MapContainer center={initial || UAE} zoom={initial ? 16 : 8} scrollWheelZoom className="w-full h-full" style={{ zIndex: 0 }}>
            <TileLayer url={TILES} attribution="&copy; OpenStreetMap" maxZoom={19} />
            <Picker pos={pos} setPos={setPos} />
            <FlyTo target={fly} />
          </MapContainer>
          <MapSearch onPick={pick} L={L} lang={lang} />
          <button type="button" onClick={locate} className="absolute bottom-4 end-4 z-[1000] w-12 h-12 rounded-full bg-white shadow-lg border border-slate-200 flex items-center justify-center text-[#064E3B]" title={L("موقعي الحالي", "My location")} data-testid="office-map-locate">
            {locating ? <Loader2 className="w-5 h-5 animate-spin" /> : <LocateFixed className="w-5 h-5" />}
          </button>
          {!pos && <div className="absolute bottom-4 start-4 z-[1000] bg-white/95 rounded-xl shadow px-3 py-2 text-xs font-semibold text-slate-700">{L("اضغط على الخارطة لوضع الدبوس", "Tap the map to drop the pin")}</div>}
        </div>
        <div className="flex items-center gap-3 px-5 py-4 border-t border-slate-100">
          <p className="flex-1 text-xs text-slate-500" dir="ltr" data-testid="office-map-coords">{pos ? `${pos[0].toFixed(5)}, ${pos[1].toFixed(5)}` : ""}</p>
          <button type="button" onClick={onClose} className="h-11 px-5 rounded-xl text-sm font-bold text-slate-600 hover:bg-slate-100" data-testid="office-map-cancel">{L("إلغاء", "Cancel")}</button>
          <button type="button" disabled={!pos} onClick={() => onSave(pos)} className="h-11 px-6 rounded-xl bg-[#064E3B] text-white text-sm font-bold flex items-center gap-2 disabled:opacity-50" data-testid="office-map-confirm"><Check className="w-4 h-4" />{L("تأكيد الموقع", "Confirm location")}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Office location: interactive map picker storing lat/lng + a Google Maps link (map_url).
export function OfficeLocationField({ lat, lng, mapUrl, onChange, L, lang, labels }) {
  const tx = { pick: L("اختر موقع المكتب على الخارطة", "Pick the office location on the map"), set: L("تم تحديد موقع المكتب", "Office location set"), ...labels };
  const [open, setOpen] = useState(false);
  const pos = parseLatLng(lat, lng, mapUrl);
  const save = (p) => { onChange({ lat: p[0], lng: p[1], map_url: mapLink(p) }); setOpen(false); };
  return (
    <div data-testid="office-location-field">
      {pos ? (
        <div className="rounded-2xl border-2 border-[#064E3B]/25 bg-[#E6EFEB]/50 overflow-hidden">
          <button type="button" onClick={() => setOpen(true)} className="block w-full h-40 relative" data-testid="office-location-preview">
            <MapContainer key={pos.join(",")} center={pos} zoom={15} dragging={false} zoomControl={false} scrollWheelZoom={false} doubleClickZoom={false} touchZoom={false} keyboard={false} attributionControl={false} className="w-full h-full pointer-events-none" style={{ zIndex: 0 }}>
              <TileLayer url={TILES} />
              <Marker position={pos} icon={PIN} />
            </MapContainer>
          </button>
          <div className="flex flex-wrap items-center gap-2 p-3">
            <span className="flex-1 min-w-0 text-sm font-semibold text-[#064E3B]">
              <span className="flex items-center gap-2"><MapPin className="w-4 h-4 shrink-0" />{tx.set}</span>
              <span className="block text-[11px] text-slate-500 mt-0.5" dir="ltr" data-testid="office-location-coords">{pos[0].toFixed(5)}, {pos[1].toFixed(5)}</span>
            </span>
            <a href={mapLink(pos)} target="_blank" rel="noreferrer" className="h-9 px-3 rounded-xl border border-slate-200 bg-white text-xs font-bold flex items-center gap-1.5" data-testid="office-location-open"><ExternalLink className="w-3.5 h-3.5" />{L("عرض على الخريطة", "View on map")}</a>
            <button type="button" onClick={() => setOpen(true)} className="h-9 px-3 rounded-xl bg-[#064E3B] text-white text-xs font-bold flex items-center gap-1.5" data-testid="office-location-change"><Pencil className="w-3.5 h-3.5" />{L("تغيير الموقع", "Change location")}</button>
            <button type="button" onClick={() => onChange({ lat: null, lng: null, map_url: "" })} className="h-9 w-9 rounded-xl border border-red-200 bg-white text-red-600 flex items-center justify-center" title={L("حذف الموقع", "Remove location")} data-testid="office-location-clear"><X className="w-4 h-4" /></button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => setOpen(true)} className="w-full h-14 rounded-2xl border-2 border-dashed border-slate-300 hover:border-[#064E3B] bg-white flex items-center justify-center gap-2 text-sm font-bold text-[#064E3B] transition-colors" data-testid="office-location-pick">
          <MapPin className="w-5 h-5" />{tx.pick}
        </button>
      )}
      {open && <MapDialog initial={pos} onSave={save} onClose={() => setOpen(false)} L={L} lang={lang} />}
    </div>
  );
}
